/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * Reset Password after sending email on Forgot Password Form
 */

import {
  Button,
  Heading,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import TextField from "components/base/atoms/TextField";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { setFormErrors } from "utils";
import {
  useForgotPasswordQuery,
  useResendCodeQuery,
  useResetPasswordQuery,
} from "../../_actions/AuthActions";
import { ResetPasswordSchema } from "../../config/schema/ResetPasswordSchema";
import { ModalTypes } from "../../config/types/modals";
import useAuth from "../../hooks/useAuth";
import { SecondaryButton } from "../base/atoms/HJSButton";
import {
  ErrorToastHandler,
  SuccessToastHandler,
} from "../helpers/ToastHelpers";

interface IResetPasswordValues {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

interface IModalComponentProps {
  changeModalType: (modalType: ModalTypes) => void;
}
type TModalComponent = (props: IModalComponentProps) => JSX.Element;

const ResetPassword: TModalComponent = ({
  changeModalType,
}: IModalComponentProps) => {
  const toast = useToast();

  const { authState: { email = "" } = {} } = useAuth();
  const initialFormValues: IResetPasswordValues = {
    code: "",
    newPassword: "",
    confirmPassword: "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm<IResetPasswordValues>({
    resolver: yupResolver(ResetPasswordSchema),
    defaultValues: initialFormValues,
  });

  const [showPassword, setShowPassword] = useState(false);
  const handleShowPassword: () => void = () => setShowPassword(!showPassword);
  // const [resendLabel, setResendLabel] = useState(
  //   "Haven't received the code yet?"
  // );
  const [isDisabled, setIsDisabled] = useState(false);

  const { mutate: resetPassword, isLoading } = useResetPasswordQuery();
  const { mutate: resendCode, isLoading: loadingResendCode } =
    useForgotPasswordQuery();

  const submitHandler: (values: IResetPasswordValues) => void = (
    values: IResetPasswordValues
  ) => {
    resetPassword(
      {
        email,
        code: values.code,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          SuccessToastHandler({
            toast,
            title: "Success! 🎉",
            description:
              "Your password has been reset, please log into your account.",
          });
          changeModalType(ModalTypes.LOGIN);
        },

        onError: (error) => {
          setFormErrors(error, setError);
        },
      }
    );
  };

  const resendHandler: () => void = () => {
    resendCode(
      { email },
      {
        onSuccess: async () => {
          SuccessToastHandler({
            toast,
            title: "Success! 🎉",
            description: "We've sent a verification code to your email",
          });
          // setResendLabel("Haven't received the code yet?");
        },
        onError: () => {
          ErrorToastHandler({
            toast,
            title: "Error",
            description:
              "Some error occurred while sending verification code to your email",
          });
          // setResendLabel("Haven't received the code yet?");
          // setResendLabel("An error occurred.");
        },
      }
    );
  };
  return (
    <ModalContent px={6} py={4}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <ModalHeader>
          <VStack align="flex-start">
            <Heading as="h2" fontSize="2.5rem" color="teal.900">
              Forgot Password?
            </Heading>
            <Text color="hjsBlack" fontWeight="500" fontSize="1rem">
              We have sent a code on your email to verify if it is you. Please
              check your email.
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" maxH="85vh">
          <VStack mt={4} alignItems="flex-start" gap={4}>
            <TextField
              label="Confirmation Code"
              placeholder="Enter the 6-digit code"
              error={Boolean(errors.code)}
              helperText={errors.code?.message}
              {...register("code")}
            />

            <TextField
              label="New Password"
              placeholder="Minimum 8 digits"
              error={Boolean(errors.newPassword)}
              helperText={errors.newPassword?.message}
              {...register("newPassword")}
              type="password"
            />
            <TextField
              label="Confirm Password"
              placeholder="Re-type your password"
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword?.message}
              {...register("confirmPassword")}
              type="password"
            />
          </VStack>
        </ModalBody>
        <ModalFooter alignItems="center" justifyContent="center">
          <VStack my={4} spacing={4}>
            <SecondaryButton isLoading={isLoading} type="submit">
              Reset Password
            </SecondaryButton>
            <Button
              fontWeight="400"
              color="hjsBlack"
              variant="link"
              textDecor="underline"
              disabled={loadingResendCode}
              onClick={resendHandler}
            >
              {"Haven't"} received the code yet?
            </Button>
          </VStack>
        </ModalFooter>
      </form>
    </ModalContent>
  );
};

export default ResetPassword;
